import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, KeyRound, ShieldCheck, AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [step, setStep] = useState(1); // 1=email, 2=question+answer+newPwd
  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState(null); // {id, fr, en}
  const [answer, setAnswer] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inputCls =
    "w-full px-3 py-2.5 text-sm rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-[#E11D48] focus:outline-none";

  const lookup = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post("/security/lookup", { email });
      if (!data.has_question) {
        setError("Aucune question secrète n'est configurée pour ce compte. Contactez l'admin via /contact.");
        return;
      }
      // Fetch question label
      const qResp = await api.get("/security/questions");
      const found = (qResp.data.questions || []).find((q) => q.id === data.question_id);
      setQuestion(found || { id: data.question_id, fr: data.question_id });
      setStep(2);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPwd !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (newPwd.length < 8) {
      setError("Le nouveau mot de passe doit faire au moins 8 caractères.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/security/reset", { email, answer: answer.trim(), new_password: newPwd });
      // Hydrate auth context from /auth/me
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch { /* still proceed */ }
      toast.success("Mot de passe réinitialisé. Vous êtes connecté ✅");
      navigate("/account");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="forgot-password-page">
      <Seo title="Mot de passe oublié" description="Réinitialiser votre mot de passe Cartonomie via votre question secrète." />
      <div className="max-w-md mx-auto px-6 lg:px-10 pt-16 pb-20">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-6 transition">
          <ArrowLeft className="w-3 h-3" /> Retour à la connexion
        </Link>

        <span className="text-[10px] tracking-[0.18em] text-slate-400 font-semibold">// PASSWORD RECOVERY</span>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-2 mb-2">
          Mot de passe oublié
        </h1>
        <p className="text-sm text-slate-600 mb-8">
          {step === 1
            ? "Entrez votre e-mail pour récupérer votre question secrète."
            : "Répondez à votre question secrète puis choisissez un nouveau mot de passe."}
        </p>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8 text-[10px] uppercase tracking-wider font-bold">
          <span className={`flex items-center gap-1 ${step >= 1 ? "text-slate-900" : "text-slate-400"}`}>
            {step > 1 ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <span className="w-4 h-4 rounded-full bg-slate-900 text-white flex items-center justify-center text-[9px]">1</span>}
            E-mail
          </span>
          <span className="text-slate-300">—</span>
          <span className={`flex items-center gap-1 ${step >= 2 ? "text-slate-900" : "text-slate-400"}`}>
            <span className={`w-4 h-4 rounded-full ${step >= 2 ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"} flex items-center justify-center text-[9px]`}>2</span>
            Réponse & nouveau mot de passe
          </span>
        </div>

        {step === 1 && (
          <form onSubmit={lookup} className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
                <Mail className="w-3 h-3 inline mr-1" /> E-mail du compte
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
                data-testid="forgot-email-input"
                required
                autoFocus
                className={inputCls}
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span data-testid="forgot-error">{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={!email || loading}
              data-testid="forgot-lookup-btn"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {loading ? "Recherche…" : "Continuer"}
            </button>
          </form>
        )}

        {step === 2 && question && (
          <form onSubmit={reset} className="space-y-4">
            <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Votre question secrète
              </div>
              <p className="text-sm font-heading font-bold text-slate-900" data-testid="forgot-question-label">
                {question.fr || question.en || question.id}
              </p>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
                Votre réponse
              </label>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Votre réponse"
                data-testid="forgot-answer-input"
                required
                autoFocus
                autoComplete="off"
                minLength={2}
                maxLength={120}
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
                Nouveau mot de passe
              </label>
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Minimum 8 caractères"
                data-testid="forgot-new-pwd"
                required
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
                Confirmer
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Retapez le mot de passe"
                data-testid="forgot-confirm-pwd"
                required
                autoComplete="new-password"
                className={`${inputCls} ${confirm && confirm !== newPwd ? "border-rose-500" : ""}`}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span data-testid="forgot-error">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-reset-btn"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {loading ? "Réinitialisation…" : "Réinitialiser et se connecter"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
